import { z } from 'zod';
import { Role } from './common.js';

// Custom claim keys. Namespaced with `serp_` so that when this app is later
// folded into the shared "hub" app's Firebase project, our claims cannot
// collide with the hub's existing RBAC claims. (See CLAUDE.md §23 Change Log
// entry for the separate-project + fold-forward convention decision.)
export const CLAIM_KEYS = {
  role: 'serp_role',
  clientId: 'serp_clientId',
  assignedClientIds: 'serp_assignedClientIds',
} as const;

export type ClaimKey = (typeof CLAIM_KEYS)[keyof typeof CLAIM_KEYS];

// The exact shape of the `request.auth.token` claims our Firestore rules and
// callables read. Server-side: set via admin.auth().setCustomUserClaims.
export const AuthClaims = z.discriminatedUnion('serp_role', [
  z.object({
    serp_role: z.literal('admin'),
  }),
  z.object({
    serp_role: z.literal('project_coordinator'),
    serp_assignedClientIds: z.array(z.string().min(1)).max(40),
  }),
  z.object({
    serp_role: z.literal('client'),
    serp_clientId: z.string().min(1),
  }),
]);
export type AuthClaims = z.infer<typeof AuthClaims>;

// Helper input for setUserRole callable (Phase 3). We validate the caller's
// intent with the un-prefixed, human-readable `role` field and translate to
// namespaced claim keys server-side.
export const SetUserRoleInput = z.discriminatedUnion('role', [
  z.object({ uid: z.string().min(1), role: z.literal('admin') }),
  z.object({
    uid: z.string().min(1),
    role: z.literal('project_coordinator'),
    assignedClientIds: z.array(z.string().min(1)).max(40),
  }),
  z.object({
    uid: z.string().min(1),
    role: z.literal('client'),
    clientId: z.string().min(1),
  }),
]);
export type SetUserRoleInput = z.infer<typeof SetUserRoleInput>;

// Translates human-readable role input into the claim object that gets set
// on the Firebase Auth user. Keep all namespacing logic here so callers
// never hand-build the raw object.
export function toAuthClaims(input: SetUserRoleInput): AuthClaims {
  switch (input.role) {
    case 'admin':
      return { serp_role: 'admin' };
    case 'project_coordinator':
      return {
        serp_role: 'project_coordinator',
        serp_assignedClientIds: input.assignedClientIds,
      };
    case 'client':
      return { serp_role: 'client', serp_clientId: input.clientId };
  }
}

// Utility for client code to read role consistently.
export function readRole(token: Record<string, unknown>): Role | null {
  const raw = token[CLAIM_KEYS.role];
  const parsed = Role.safeParse(raw);
  return parsed.success ? parsed.data : null;
}
